import type { ReaderAiPersistedState } from "@/lib/reader-ai-threads";
import type { ReaderAiThread, ToofiProductMode } from "@/types/agentic-chat";
import type { StudentToofiSurface } from "@/types/student-toofi";

const THREADS_KEY = "athr.student.toofi.threads";
const ACTIVE_THREAD_KEY = "athr.student.toofi.active-thread";
const COMPOSER_DRAFT_KEY = "athr.student.toofi.composer-draft";
export const STUDENT_TOOFI_STATE_EVENT = "athr:student-toofi-state";

const now = () => Date.now();

const readJson = <T>(key: string): T | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const writeJson = (key: string, value: unknown) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent(STUDENT_TOOFI_STATE_EVENT));
  } catch {
    // best effort only
  }
};

const genId = (prefix: string) => `${prefix}-${now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeThread = (thread: ReaderAiThread): ReaderAiThread => ({
  ...thread,
  scope: thread.scope === "resource" ? "resource" : "workspace",
  archived: Boolean(thread.archived),
  pinned: Boolean(thread.pinned),
  archivedAt: typeof thread.archivedAt === "number" ? thread.archivedAt : thread.archived ? thread.updatedAt ?? now() : null,
  memoryMode: thread.memoryMode === "temporary" ? "temporary" : "default",
  artifactCount: typeof thread.artifactCount === "number" ? Math.max(0, thread.artifactCount) : 0,
  lastArtifactAt: typeof thread.lastArtifactAt === "number" ? thread.lastArtifactAt : null,
  branchSourceThreadId: typeof thread.branchSourceThreadId === "string" ? thread.branchSourceThreadId : null,
  referenceMode: thread.referenceMode ?? "none",
});

const normalizePersistedState = (data: Partial<ReaderAiPersistedState> | null | undefined): ReaderAiPersistedState => ({
  threads: Array.isArray(data?.threads) ? data.threads.map(normalizeThread) : [],
  messagesByThread: data?.messagesByThread && typeof data.messagesByThread === "object" ? data.messagesByThread : {},
  threadSummariesByThread:
    data?.threadSummariesByThread && typeof data.threadSummariesByThread === "object" ? data.threadSummariesByThread : {},
  pinnedBlockIds: Array.isArray(data?.pinnedBlockIds) ? data.pinnedBlockIds : [],
  savedAt: typeof data?.savedAt === "number" ? data.savedAt : now(),
});

export const createStudentToofiThread = ({
  surfaceOrigin,
  linkedResourceId,
  linkedPlanId,
  productMode,
}: {
  surfaceOrigin: StudentToofiSurface;
  linkedResourceId?: string | null;
  linkedPlanId?: string | null;
  productMode?: ToofiProductMode | null;
}): ReaderAiThread => ({
  id: genId("thread"),
  resourceId: linkedResourceId ?? "__workspace__",
  scope: "workspace",
  surfaceOrigin,
  linkedPlanId: linkedPlanId ?? null,
  title: "Toofi chat",
  createdAt: now(),
  updatedAt: now(),
  archived: false,
  messageCount: 0,
  summaryVersion: 0,
  lastTopicLabel: null,
  warnedLongThreadAt: null,
  currentProductMode: productMode ?? null,
  pinned: false,
  archivedAt: null,
  memoryMode: "default",
  artifactCount: 0,
  lastArtifactAt: null,
  branchSourceThreadId: null,
  referenceMode: "none",
});

export const readStudentToofiState = (): ReaderAiPersistedState => {
  const data = readJson<ReaderAiPersistedState>(THREADS_KEY);
  return normalizePersistedState(data);
};

export const writeStudentToofiState = (state: ReaderAiPersistedState) => {
  writeJson(THREADS_KEY, { ...state, savedAt: now() });
};

export const readStudentToofiActiveThreadId = (): string | null => readJson<string>(ACTIVE_THREAD_KEY);

export const writeStudentToofiActiveThreadId = (threadId: string | null) => {
  if (threadId) {
    writeJson(ACTIVE_THREAD_KEY, threadId);
    return;
  }
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ACTIVE_THREAD_KEY);
    window.dispatchEvent(new CustomEvent(STUDENT_TOOFI_STATE_EVENT));
  } catch {
    // ignore
  }
};

export const readStudentToofiComposerDraft = (): string => readJson<string>(COMPOSER_DRAFT_KEY) ?? "";

export const writeStudentToofiComposerDraft = (draft: string) => {
  writeJson(COMPOSER_DRAFT_KEY, draft);
};
